import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Terminal, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1 2.6-2.1 3.4l3.4 2.6c2-1.8 3.1-4.5 3.1-7.7 0-.7-.06-1.3-.18-1.9H12z" />
      <path fill="#34A853" d="M6.6 14.3l-.77.6-2.7 2.1C4.8 20 8.1 22 12 22c2.7 0 5-.9 6.7-2.4l-3.4-2.6c-.9.6-2 1-3.3 1-2.5 0-4.7-1.7-5.4-4z" />
      <path fill="#4A90D9" d="M3.13 7c-.7 1.4-1.1 3-1.1 4.9 0 1.9.4 3.5 1.1 4.9l3.5-2.7C6.4 13.4 6.2 12.7 6.2 12s.2-1.4.4-2.1L3.13 7z" />
      <path fill="#FBBC05" d="M12 6c1.5 0 2.8.5 3.8 1.5l2.8-2.8C16.9 3 14.7 2 12 2 8.1 2 4.8 4 3.13 7l3.5 2.9C7.3 7.6 9.5 6 12 6z" />
    </svg>
  );
}

export default function Login() {
  const { user, startGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const oauthError = new URLSearchParams(window.location.search).get("oauth_error");

  useEffect(() => {
    if (user && user !== null) navigate("/", { replace: true });
  }, [user, navigate]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-950 text-slate-100 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.15]" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #6366F1 0, transparent 40%), radial-gradient(circle at 80% 70%, #10B981 0, transparent 40%)" }} />
        <div className="relative flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Terminal className="h-6 w-6" />
          </span>
          <span className="font-display font-bold text-xl">InfraPilot</span>
        </div>
        <div className="relative space-y-4 max-w-md">
          <h1 className="font-display text-4xl font-bold leading-tight">
            Contribute operational procedures your team can actually follow.
          </h1>
          <p className="text-slate-400 leading-relaxed">
            Sign in with Google to create and manage runbooks. You can edit and delete the ones you author — admins
            can manage everything.
          </p>
          <div className="flex gap-3 pt-2">
            <span className="font-mono text-xs rounded border border-slate-700 bg-slate-900 px-2 py-1 text-emerald-400">kubectl rollout undo</span>
            <span className="font-mono text-xs rounded border border-slate-700 bg-slate-900 px-2 py-1 text-emerald-400">pg_ctl promote</span>
          </div>
        </div>
        <p className="relative text-xs text-slate-500 font-mono">v3.0.0 — Google OAuth</p>
      </div>

      {/* right sign-in */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm space-y-6 tr-fade-up">
          <div className="lg:hidden flex items-center gap-2.5 justify-center">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Terminal className="h-5 w-5" />
            </span>
            <span className="font-display font-bold text-lg">InfraPilot</span>
          </div>

          <div>
            <h2 className="font-display text-2xl font-bold inline-flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" /> Sign in to contribute
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Browsing is open to everyone. Sign in only to create or manage runbooks.
            </p>
          </div>

          {oauthError && (
            <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              Google sign-in could not be completed. Please try again.
            </div>
          )}

          <Button
            variant="outline"
            className="w-full gap-3 h-11 text-base"
            onClick={() => startGoogleLogin("/")}
            data-testid="google-signin-btn"
          >
            <GoogleIcon /> Continue with Google
          </Button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-sm text-muted-foreground hover:text-foreground hover:underline"
              data-testid="back-to-library-link"
            >
              ← Back to the runbook library
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
